import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getRequest, submitQuote, acceptQuote, submitInfoRequest } from '../services/api';
import { getCategoryLabel, decodeHtmlEntities } from '../utils/categoryUtils';
import { getUrgencyLabel } from '../utils/urgencyUtils';
import { getRequestState, formatShortDate, REQUEST_STATE_COLORS } from '../utils/requestState';
import { formatBudgetValue } from '../utils/budgetUtils';
import { SUPPORT_PHONE_DISPLAY } from '../utils/contactInfo';
import { BadgeDisplay, MarkComplete } from './ReputationComponents';
import DisputeForm from './DisputeForm';
import SealedBidPanel from './SealedBidPanel';

interface Quote {
  id: number;
  price: string;
  message: string;
  warranty_info: string;
  estimated_delivery: string;
  status: string;
  business_name: string;
  created_at: string;
  business_id: number;
  business_user_id: number;
  badge_level: 'none' | 'verified' | 'trusted' | 'elite';
  rating_avg: number;
  rating_count: number;
  jobs_completed: number;
  member_since: string;
}

// QUOTE-INFO-REQUEST1 Phase C. Mirrors the backend's info_requests rows
// (quote_info_requests.* plus the businesses/users join). Note what is NOT
// here and never will be: price, and status. Without a status field the
// "Accept This Quote" condition (isOwner && isOpen && status === 'pending')
// cannot be expressed against this type at all.
interface InfoRequest {
  id: number;
  request_id: number;
  business_id: number;
  message: string;
  created_at: string;
  business_name: string;
  business_user_id: number;
  // INFO-REQUEST-NEXT-STEP-COPY1 Phase A added this. Optional AND nullable on
  // purpose: it is sourced from users.phone, which is NULL for 8 of 9 live
  // contractors, and the backend only includes it for authorized viewers, so
  // the key can be absent entirely. Never assume a usable string.
  business_phone?: string | null;
}

interface Completion {
  id: number;
  business_user_id: number;
  rating_submitted: boolean;
}

interface RequestData {
  id: number;
  title: string;
  description: string;
  category: string;
  urgency: string;
  budget_min: string | null;
  budget_max: string | null;
  city?: string;
  location: string;
  status: string;
  // /quotes/requests/:id selects qr.*, so expires_at is already on the wire.
  expires_at?: string | null;
  customer_name: string;
  customer_phone?: string;
  user_id: number;
  created_at: string;
}

// INFO-REQUEST-NEXT-STEP-COPY1 Phase B. The homeowner may reach the dashboard
// before the email, so the same next-step nudge belongs on the card.
//
// One trim decides the variant, so null, undefined, '' and '   ' all collapse
// to the phone-free copy. Rendering "at  ." would be worse than not naming a
// number at all. PLAIN TEXT by design — no tel: link. Making the phone
// tappable is parked as INFO-REQUEST-TAPPABLE-PHONE1 because it introduces
// attribute-context escaping this ticket does not need.
const infoRequestNextStep = (businessName: string, phone?: string | null): string => {
  const trimmedPhone = typeof phone === 'string' ? phone.trim() : '';
  const tail = 'The sooner they have the details, the sooner you can get a real price.';

  return trimmedPhone
    ? `Reply directly to ${businessName} at ${trimmedPhone}. A quick text with photos is usually fastest. ${tail}`
    : `Reply directly to ${businessName}. A quick message with photos is usually fastest. ${tail}`;
};

// QUOTE-PRICE-VALIDATION1 Phase A. A quote at or above this dollar figure gets
// an inline confirmation step before the POST fires. Named, not an inline
// literal, so the number is tunable in one place and greppable.
//
// This is a flat threshold ON PURPOSE. It is deliberately NOT compared against
// the request's budget band — that rule was tested against the live quote set
// and rejected. Nothing in this file reads budget_min or budget_max for
// validation, and nothing here suggests, estimates or anchors a price.
const LARGE_QUOTE_CONFIRM_THRESHOLD = 10000;

// 17000 -> "$17,000". Whole dollars only: the price input carries no `step`
// attribute, so the browser's default step of 1 already blocks cents from ever
// being entered. Rounding here is belt-and-braces for a value that arrives
// integral, not a silent truncation of something the contractor typed.
const formatQuoteAmount = (amount: number): string =>
  `$${Math.round(amount).toLocaleString('en-US')}`;

const NO_QUOTES_HEADING = 'No quotes yet';
const NO_QUOTES_BODY = "We're notifying contractors who match your request and service area. Quotes may take a little time because contractors review the job details before responding.";
const NO_QUOTES_FOOTER = `You can check back here for updates. If you need help, call QuoteBid at ${SUPPORT_PHONE_DISPLAY}.`;

const RequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<RequestData | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [quoteCount, setQuoteCount] = useState<number | undefined>(undefined);
  const [completion, setCompletion] = useState<Completion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [outsideFootprint, setOutsideFootprint] = useState(false);
  // QUOTE-PRICE-VALIDATION1 Phase A. null = the quote form is showing normally.
  // A number = the confirmation step is showing for that already-parsed amount.
  //
  // Holding the NUMBER rather than a boolean is the whole point: the price is
  // read and parsed once, in handleQuoteSubmit, and this value is what both the
  // confirmation copy and the POST use. quoteForm.price is never re-read after
  // the guard, so a stale or mid-edit field value cannot reach the wire.
  const [pendingQuotePrice, setPendingQuotePrice] = useState<number | null>(null);
  // Guards the POST itself. `submitting` state drives the disabled attribute,
  // which stops clicks but is not a guard on the handler — a ref flips
  // synchronously and so holds across the await where state would not.
  const submitInFlightRef = useRef(false);
  // Info-request state is entirely separate from quote state. Nothing below
  // feeds hasSubmittedQuote or the quote form's visibility.
  const [infoRequests, setInfoRequests] = useState<InfoRequest[]>([]);
  const [showInfoForm, setShowInfoForm] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [infoSubmitting, setInfoSubmitting] = useState(false);
  const [infoError, setInfoError] = useState('');

  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!) : null;

  const [quoteForm, setQuoteForm] = useState({
    price: '',
    message: '',
    warranty_info: '',
    estimated_delivery: '',
  });

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const response = await getRequest(Number(id));
      setRequest(response.data.request);
      setQuotes(response.data.quotes);
      setQuoteCount(response.data.quote_count);
      // Empty-safe by construction: anything that is not an actual array —
      // key absent (cached response or a pre-Phase-B deploy), null, or an
      // error shape — collapses to []. The page renders normally with no
      // info-request section rather than throwing on .length / .map.
      setInfoRequests(Array.isArray(response.data?.info_requests) ? response.data.info_requests : []);
      setCompletion(response.data.completion ?? null);
    } catch (err) {
      setError('Failed to load request');
    } finally {
      setLoading(false);
    }
  };

// The only place a quote POST is issued. Takes the price as an argument rather
// than reading quoteForm.price so the amount the contractor confirmed is
// provably the amount that is sent. The payload shape is unchanged.
const performQuoteSubmit = async (price: number) => {
  if (submitInFlightRef.current) return;
  submitInFlightRef.current = true;

  setSubmitting(true);
  setSubmitError('');

  try {
    await submitQuote({
      request_id: Number(id),
      business_id: user?.id,
      price,
      message: quoteForm.message,
      warranty_info: quoteForm.warranty_info,
      estimated_delivery: quoteForm.estimated_delivery,
    });

    // Ordering below is load-bearing and unchanged: hide the form FIRST, then
    // refetch. hasSubmittedQuote is derived from the refetched quotes array, so
    // it is stale until fetchRequest resolves; setShowQuoteForm(false) is what
    // covers that window. Clearing the confirm state first only unmounts the
    // panel we own and does not touch that pair.
    setPendingQuotePrice(null);
    setShowQuoteForm(false);
    fetchRequest();
  } catch (err: any) {
    // Drop out of the confirmation step on ANY failure so the contractor lands
    // back on the editable form with the error, rather than being stranded on a
    // panel whose only forward action just failed.
    setPendingQuotePrice(null);
    if (err.response?.data?.code === 'OUTSIDE_SERVICE_FOOTPRINT'
        || (err.response?.status === 403
            && /outside your selected service footprint/i.test(err.response?.data?.error || ''))) {
      setOutsideFootprint(true);
    } else {
      setSubmitError(err.response?.data?.error || 'Failed to submit quote');
    }
  } finally {
    submitInFlightRef.current = false;
    setSubmitting(false);
  }
};

const handleQuoteSubmit = (e: React.FormEvent) => {
  e.preventDefault();
  if (submitInFlightRef.current) return;

  // Read and parse the price exactly ONCE. Everything downstream — the
  // confirmation copy, the button label, the POST — uses `price`, never the
  // form field again.
  const price = Number(quoteForm.price.trim());

  // type="number" already blocks non-numeric text and (via the default step of
  // 1) decimals. It does NOT block zero or a negative, and the backend's only
  // check is truthiness, which rejects 0 as a missing field and lets a negative
  // through. This is the guard for those two.
  if (!Number.isFinite(price) || price <= 0) {
    setSubmitError('Enter a quote amount greater than $0.');
    return;
  }

  setSubmitError('');

  if (price >= LARGE_QUOTE_CONFIRM_THRESHOLD) {
    setPendingQuotePrice(price);
    return;
  }

  performQuoteSubmit(price);
};

const handleInfoRequestSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  const trimmed = infoMessage.trim();
  if (!trimmed) {
    setInfoError('Please enter a question for the homeowner.');
    return;
  }

  setInfoSubmitting(true);
  setInfoError('');

  try {
    // MESSAGE ONLY. No price is collected by the form and none is sent here.
    // The backend rejects any price field with 400 INFO_REQUEST_HAS_PRICE.
    await submitInfoRequest({
      request_id: Number(id),
      message: trimmed,
    });

    setShowInfoForm(false);
    setInfoMessage('');
    fetchRequest();
  } catch (err: any) {
    const code = err.response?.data?.code;
    if (code === 'OUTSIDE_SERVICE_FOOTPRINT') {
      // Same gate as the bid path, so reuse the same notice.
      setOutsideFootprint(true);
    } else {
      // INFO_REQUEST_LIMIT_REACHED, REQUEST_MISSING_LANE and
      // REQUEST_NOT_ACCEPTING_BIDS each carry a specific backend message.
      // Surface it verbatim instead of a generic failure.
      setInfoError(err.response?.data?.error || 'Failed to send your information request');
    }
  } finally {
    setInfoSubmitting(false);
  }
};

  const handleAcceptQuote = async (quoteId: number) => {
    if (!window.confirm('Accept this quote? This will close the request.')) return;
    
    try {
      await acceptQuote(quoteId);
      fetchRequest();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to accept quote');
    }
  };

  if (loading) return <div style={styles.loading}>Loading...</div>;
  if (error || !request) return <div style={styles.error}>{error || 'Request not found'}</div>;

  const isOwner = user?.id === request.user_id;
  const isBusiness = user?.user_type === 'business';
  // isOpen stays exactly as it was: raw status, no expiry term. It gates the
  // Accept button and the quote form, and the backend does NOT gate bidding on
  // expiry — an expired request keeps status='open' and stays biddable via
  // direct link. Folding expiry in here would silently change who can bid.
  const isOpen = request.status === 'open';
  // Display-only state, derived alongside it.
  const requestState = getRequestState(request.status, request.expires_at);

  // BI2 sealed-bid viewer classification. quote_count (from BI1) is the source
  // of truth for total submitted quotes; fall back to quotes.length only if a
  // stale response during a deploy transition omits it.
  const token = localStorage.getItem('token');
  const isAuthed = !!token && !!user;
  const isAdmin = user?.is_admin === true || user?.id === 110;
  const isContractor = isAuthed && isBusiness && !isAdmin && !isOwner;
  const isCustomerViewer = isAuthed && !isBusiness && !isAdmin && !isOwner;
  const hasSubmittedQuote = quotes.some((q) => q.business_id === user?.id);
  const displayedQuoteCount = quoteCount ?? quotes.length;

  const renderQuoteCard = (quote: Quote) => (
    <div key={quote.id} style={{
      ...styles.quoteCard,
      borderColor: quote.status === 'accepted' ? '#27ae60' : quote.status === 'rejected' ? '#e74c3c' : '#ddd'
    }}>
      <div style={styles.quoteHeader}>
        <h3>{quote.business_name}</h3>
        <span style={styles.price}>${quote.price}</span>
      </div>

      <BadgeDisplay
        badgeLevel={quote.badge_level}
        ratingAvg={quote.rating_avg}
        ratingCount={quote.rating_count}
        size="sm"
      />
      <div style={styles.memberSince}>
        Member since {new Date(quote.member_since).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
      </div>

      <p>{decodeHtmlEntities(quote.message)}</p>

      <div style={styles.quoteMeta}>
       {quote.warranty_info && <span>🛡️ {decodeHtmlEntities(quote.warranty_info)}</span>}
       {quote.estimated_delivery && <span>🚚 {decodeHtmlEntities(quote.estimated_delivery)}</span>}
        <span style={{
          ...styles.quoteStatus,
          backgroundColor: quote.status === 'accepted' ? '#27ae60' : quote.status === 'rejected' ? '#e74c3c' : '#f39c12'
        }}>
          {quote.status}
        </span>
      </div>

      {isOwner && isOpen && quote.status === 'pending' && (
        <button onClick={() => handleAcceptQuote(quote.id)} style={styles.acceptButton}>
          ✅ Accept This Quote
        </button>
      )}
    </div>
  );

  // An info request is NOT a bid. This renderer is entirely separate from
  // renderQuoteCard: no price, no dollar sign, no status pill, no "quote
  // received" framing, and — structurally — no accept control. InfoRequest has
  // no status field, so the quote card's accept condition cannot be written
  // here even by accident.
  const renderInfoRequestCard = (infoRequest: InfoRequest) => (
    <div key={`info-${infoRequest.id}`} style={styles.infoCard}>
      <div style={styles.infoCardHeader}>
        <span style={styles.infoBadge}>Information request</span>
        <span style={styles.infoDate}>{formatShortDate(infoRequest.created_at)}</span>
      </div>

      <p style={styles.infoLede}>
        <strong>{infoRequest.business_name}</strong> requested more information before submitting a quote.
      </p>

      {/* pre-line matches the email template so multi-line questions keep
          their line breaks exactly as the contractor typed them. */}
      <p style={styles.infoMessage}>{decodeHtmlEntities(infoRequest.message)}</p>

      {/* Homeowner only. isOwner is the request's own user_id check, so a
          contractor reading back their own question is never told to reply to
          themselves, and neither is an admin viewing the request for support.
          Its own <p>, so styles.infoMessage and its pre-line are untouched. */}
      {isOwner && (
        <p style={styles.infoNextStep}>
          {infoRequestNextStep(infoRequest.business_name, infoRequest.business_phone)}
        </p>
      )}
    </div>
  );

  return (
    <div style={styles.container}>
      <button onClick={() => navigate(-1)} style={styles.backButton}>← Back</button>
      
      <div style={styles.requestCard}>
        <div style={styles.header}>
          <span style={{ ...styles.status, backgroundColor: REQUEST_STATE_COLORS[requestState].color }}>
            {requestState}
          </span>
          <span style={styles.category}>{getCategoryLabel(request.category)}</span>
        </div>

        {requestState === 'Expired' && (
          <div style={styles.stateNote}>
            This request expired on {formatShortDate(request.expires_at)} and is no longer shown to businesses browsing QuoteBid.
          </div>
        )}
        {requestState === 'Closed' && (
          <div style={{ ...styles.stateNote, background: '#f1f5f9', borderColor: '#e2e8f0', color: '#475569' }}>
            This request is closed and is no longer shown to businesses browsing QuoteBid.
          </div>
        )}

        <h1>{request.title}</h1>
        <p style={{ ...styles.description, whiteSpace: 'pre-line' }}>{decodeHtmlEntities(request.description)}</p>

        <div style={styles.info}>
          <div style={styles.infoItem}>
            <strong>📍 Location:</strong> {request.city || request.location || 'Not specified'}
          </div>
          <div style={styles.infoItem}>
            <strong>💰 Budget:</strong> {formatBudgetValue(request.budget_min, request.budget_max)}
          </div>
          <div style={styles.infoItem}>
            <strong>⏰ Urgency:</strong> {getUrgencyLabel(request.urgency)}
          </div>
          <div style={styles.infoItem}>
            <strong>👤 Posted by:</strong> {request.customer_name}
          </div>
          {isBusiness && request.customer_phone && (
            <div style={styles.infoItem}>
              <strong>📞 Phone:</strong>{' '}
              <a href={`tel:${request.customer_phone}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                {request.customer_phone}
              </a>
            </div>
          )}
        </div>
      </div>

      {outsideFootprint && (
        <div style={styles.lockedNotice}>
          <div style={styles.lockedTitle}>This request is outside your selected service footprint.</div>
          <p style={styles.lockedBody}>
            You can only submit quotes for categories and cities included in your current QuoteBid plan.
          </p>
          <p style={styles.lockedBody}>
            You can update your service footprint in <Link to="/settings" style={styles.lockedLink}>Settings</Link> if you want to serve this type of request in the future.
          </p>
        </div>
      )}

      {!outsideFootprint && isBusiness && isOpen && !showQuoteForm && !showInfoForm && !hasSubmittedQuote && (
        <button onClick={() => { setShowQuoteForm(true); setSubmitError(''); setPendingQuotePrice(null); }} style={styles.submitButton}>
          💰 Submit a Quote
        </button>
      )}

      {/* A VISIBLY SEPARATE second action. Deliberately NOT gated on
          hasSubmittedQuote: a contractor who already filed a bid must still be
          able to ask a follow-up question, and one who only asked a question
          still sees the Submit a Quote button above because info requests
          never enter the quotes array that hasSubmittedQuote reads. */}
      {!outsideFootprint && isBusiness && isOpen && !showQuoteForm && !showInfoForm && (
        <button onClick={() => { setShowInfoForm(true); setInfoError(''); }} style={styles.infoRequestButton}>
          ❓ Request More Information
        </button>
      )}

      {/* QUOTE-PRICE-VALIDATION1 Phase A. An inline phase machine in the shape
          DisputeForm.tsx already uses — NOT an overlay and NOT window.confirm.
          The quote form is already inline on this page, and the app has no
          reusable modal component (they are hand-rolled three different ways),
          so the confirmation swaps in place of the fields inside the same card.
          The field values live in `quoteForm` on the parent, so unmounting the
          inputs here preserves everything the contractor typed. */}
      {!outsideFootprint && showQuoteForm && pendingQuotePrice !== null && (
        <div style={styles.quoteForm}>
          <div style={styles.confirmPanel}>
            <h3 style={styles.confirmTitle}>Please confirm your quote amount</h3>
            <p style={styles.confirmBody}>
              You are about to submit a quote for {formatQuoteAmount(pendingQuotePrice)}.
            </p>
            <p style={styles.confirmBody}>
              Large quote amounts should be reviewed before they are sent to the homeowner.
            </p>
            <p style={styles.confirmQuestion}>Is this amount correct?</p>
          </div>
          <div style={styles.formButtons}>
            {/* Returns to the form with the price untouched and editable —
                quoteForm is never cleared, so the field repopulates as typed. */}
            <button
              type="button"
              onClick={() => setPendingQuotePrice(null)}
              disabled={submitting}
              style={styles.cancelButton}
            >
              Edit Amount
            </button>
            {/* Submits the confirmed number, not a re-read of the field. */}
            <button
              type="button"
              onClick={() => performQuoteSubmit(pendingQuotePrice)}
              disabled={submitting}
              style={styles.submitButton}
            >
              {submitting ? 'Submitting...' : `Submit ${formatQuoteAmount(pendingQuotePrice)}`}
            </button>
          </div>
        </div>
      )}

      {!outsideFootprint && showQuoteForm && pendingQuotePrice === null && (
        <div style={styles.quoteForm}>
          <h3>Submit Your Quote</h3>
          <form onSubmit={handleQuoteSubmit}>
            <div style={styles.field}>
              <label>Your Price ($)</label>
              <input
                type="number"
                value={quoteForm.price}
                onChange={(e) => setQuoteForm({...quoteForm, price: e.target.value})}
                required
                style={styles.input}
                placeholder="Enter your best price"
              />
            </div>
            <div style={styles.field}>
              <label>Message to Customer</label>
              <textarea
                value={quoteForm.message}
                onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                style={styles.textarea}
                placeholder="Describe what you're offering..."
              />
            </div>
            <div style={styles.row}>
              <div style={styles.field}>
                <label>Warranty</label>
                <input
                  type="text"
                  value={quoteForm.warranty_info}
                  onChange={(e) => setQuoteForm({...quoteForm, warranty_info: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., 30 days"
                />
              </div>
              <div style={styles.field}>
                <label>Delivery Time</label>
                <input
                  type="text"
                  value={quoteForm.estimated_delivery}
                  onChange={(e) => setQuoteForm({...quoteForm, estimated_delivery: e.target.value})}
                  style={styles.input}
                  placeholder="e.g., Same day"
                />
              </div>
            </div>
            {submitError && (
              <div style={styles.errorBanner}>{submitError}</div>
            )}
            <div style={styles.formButtons}>
              <button type="button" onClick={() => { setShowQuoteForm(false); setSubmitError(''); }} style={styles.cancelButton}>
                Cancel
              </button>
              <button type="submit" disabled={submitting} style={styles.submitButton}>
                {submitting ? 'Submitting...' : 'Submit Quote'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Its own form, outside the quote form entirely. One field, and that
          field is not a price. */}
      {!outsideFootprint && showInfoForm && (
        <div style={styles.infoForm}>
          <h3>Request More Information</h3>
          <p style={styles.infoFormNote}>
            Ask the homeowner what you need to know before you price this job. This is not a quote — no price is sent, and it does not use one of your monthly quotes.
          </p>
          <form onSubmit={handleInfoRequestSubmit}>
            <div style={styles.field}>
              <label>Your Question</label>
              <textarea
                value={infoMessage}
                onChange={(e) => setInfoMessage(e.target.value)}
                required
                style={styles.textarea}
                placeholder="e.g., What material is the door? Does the job include painting the frame?"
              />
            </div>
            {infoError && (
              <div style={styles.errorBanner}>{infoError}</div>
            )}
            <div style={styles.formButtons}>
              <button type="button" onClick={() => { setShowInfoForm(false); setInfoError(''); }} style={styles.cancelButton}>
                Cancel
              </button>
              <button type="submit" disabled={infoSubmitting} style={styles.infoSubmitButton}>
                {infoSubmitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.quotesSection}>
        {(isAdmin || isOwner) ? (
          <>
            <h2>💬 Quotes ({displayedQuoteCount})</h2>
            {quotes.length === 0 ? (
              <div style={styles.noQuotesCard}>
                <h3 style={styles.noQuotesHeading}>{NO_QUOTES_HEADING}</h3>
                <p style={styles.noQuotesBody}>{NO_QUOTES_BODY}</p>
                <p style={styles.noQuotesFooter}>{NO_QUOTES_FOOTER}</p>
              </div>
            ) : (
              quotes.map(renderQuoteCard)
            )}
          </>
        ) : isContractor && hasSubmittedQuote ? (
          <>
            <SealedBidPanel variant="submitted" quoteCount={displayedQuoteCount} />
            {quotes.filter((q) => q.business_id === user?.id).map(renderQuoteCard)}
          </>
        ) : isContractor && displayedQuoteCount > 0 ? (
          <SealedBidPanel variant="pre-submission" quoteCount={displayedQuoteCount} />
        ) : isContractor ? (
          <SealedBidPanel variant="first-quote" quoteCount={0} />
        ) : isCustomerViewer ? (
          <SealedBidPanel variant="customer-viewer" quoteCount={displayedQuoteCount} />
        ) : (
          <>
            <SealedBidPanel variant="anonymous" quoteCount={displayedQuoteCount} />
            <button onClick={() => navigate('/login')} style={styles.signInButton}>
              Sign in to submit your quote
            </button>
          </>
        )}
      </div>

      {/* Rendered OUTSIDE the quotes section so an info request can never be
          mistaken for a bid, and so nothing here affects how quotes render,
          sort or count. No client-side identity filtering: the backend already
          applies the three-way visibility filter (admin/owner see all, another
          authenticated user sees only their own, unauthenticated sees none),
          and duplicating that here would risk diverging from it. */}
      {infoRequests.length > 0 && (
        <div style={styles.infoSection}>
          <h2 style={styles.infoHeading}>Information requests ({infoRequests.length})</h2>
          {infoRequests.map(renderInfoRequestCard)}
        </div>
      )}

      {isOwner && quotes.some(q => q.status === 'accepted') && (
        <div style={styles.completionSection}>
          <h2 style={styles.completionHeading}>Job completion</h2>
          <MarkComplete
            quoteRequestId={request.id}
            businessName={quotes.find(q => q.status === 'accepted')!.business_name}
            initialCompletion={completion}
          />
          <div style={styles.disputeDivider} />
          <DisputeForm quoteRequestId={request.id} />
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '40px',
    maxWidth: '800px',
    margin: '0 auto',
  },
  backButton: {
    padding: '10px 20px',
    backgroundColor: '#ecf0f1',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  requestCard: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  },
  header: {
    display: 'flex',
    gap: '10px',
    marginBottom: '15px',
  },
  status: {
    padding: '4px 12px',
    borderRadius: '15px',
    color: 'white',
    fontSize: '12px',
    textTransform: 'uppercase',
  },
  stateNote: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    color: '#b45309',
    borderRadius: '8px',
    padding: '10px 14px',
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '15px',
    lineHeight: 1.5,
  },
  category: {
    padding: '4px 12px',
    backgroundColor: '#ecf0f1',
    borderRadius: '15px',
    fontSize: '12px',
  },
  description: {
    fontSize: '16px',
    lineHeight: '1.6',
    marginBottom: '20px',
  },
  info: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '10px',
  },
  infoItem: {
    padding: '10px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
  },
  error: {
    textAlign: 'center',
    padding: '50px',
    color: '#e74c3c',
  },
  submitButton: {
    display: 'block',
    width: '100%',
    padding: '15px',
    backgroundColor: '#27ae60',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  quoteForm: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  },
  // Amber caution treatment, matching stateNote and SealedBidPanel rather than
  // the red errorBanner — this is a checkpoint, not a failure.
  confirmPanel: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: '10px',
    padding: '22px 24px',
  },
  confirmTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: 700,
    color: '#92400e',
  },
  confirmBody: {
    margin: '0 0 10px 0',
    fontSize: '15px',
    lineHeight: 1.6,
    color: '#0f172a',
  },
  confirmQuestion: {
    margin: '14px 0 0 0',
    fontSize: '15px',
    fontWeight: 600,
    color: '#0f172a',
  },
  field: {
    marginBottom: '15px',
    flex: 1,
  },
  input: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    marginTop: '5px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    marginTop: '5px',
    minHeight: '100px',
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    gap: '15px',
  },
  formButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '20px',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#ecf0f1',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  quotesSection: {
    marginTop: '30px',
  },
  signInButton: {
    display: 'block',
    width: '100%',
    padding: '14px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '15px',
    cursor: 'pointer',
  },
  noQuotes: {
    textAlign: 'center',
    color: '#7f8c8d',
    padding: '30px',
  },
  noQuotesCard: {
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '22px 24px',
    color: '#475569',
  },
  noQuotesHeading: {
    color: '#0f172a',
    fontSize: '18px',
    fontWeight: 600,
    margin: '0 0 12px 0',
  },
  noQuotesBody: {
    margin: '0 0 12px 0',
    lineHeight: 1.5,
  },
  noQuotesFooter: {
    margin: 0,
    lineHeight: 1.5,
  },
  quoteCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    border: '2px solid #ddd',
    marginBottom: '15px',
  },
  quoteHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  price: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#27ae60',
  },
  quoteMeta: {
    display: 'flex',
    gap: '15px',
    marginTop: '15px',
    flexWrap: 'wrap',
  },
  quoteStatus: {
    padding: '4px 10px',
    borderRadius: '15px',
    color: 'white',
    fontSize: '12px',
  },
  acceptButton: {
    marginTop: '15px',
    padding: '12px 24px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
  },
  errorBanner: {
    padding: '12px 16px',
    marginBottom: '16px',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontSize: '14px',
    lineHeight: '1.4',
  },
  memberSince: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '4px',
    marginBottom: '12px',
  },
  completionSection: {
    marginTop: '30px',
  },
  completionHeading: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '16px',
  },
  disputeDivider: {
    height: '1px',
    background: '#e2e8f0',
    margin: '24px 0',
  },
  lockedNotice: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '24px',
    marginBottom: '20px',
  },
  lockedTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '8px',
  },
  lockedBody: {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: 1.6,
    margin: '4px 0',
  },
  infoSection: {
    marginTop: '30px',
  },
  infoHeading: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#0f172a',
    marginBottom: '16px',
  },
  infoCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '10px',
    border: '2px solid #bfdbfe',
    borderLeft: '5px solid #3b82f6',
    marginBottom: '15px',
  },
  infoCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  infoBadge: {
    padding: '4px 10px',
    borderRadius: '15px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontSize: '12px',
    fontWeight: 600,
  },
  infoDate: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  infoLede: {
    margin: '0 0 10px 0',
    color: '#0f172a',
    lineHeight: 1.5,
  },
  infoMessage: {
    margin: 0,
    color: '#334155',
    lineHeight: 1.6,
    whiteSpace: 'pre-line',
  },
  infoNextStep: {
    margin: '14px 0 0 0',
    paddingTop: '14px',
    borderTop: '1px solid #e2e8f0',
    color: '#475569',
    fontSize: '14px',
    lineHeight: 1.6,
  },
  infoRequestButton: {
    display: 'block',
    width: '100%',
    padding: '15px',
    backgroundColor: 'white',
    color: '#1d4ed8',
    border: '2px solid #3b82f6',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '20px',
  },
  infoForm: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    marginBottom: '20px',
  },
  infoFormNote: {
    fontSize: '14px',
    color: '#64748b',
    lineHeight: 1.6,
    margin: '0 0 16px 0',
  },
  infoSubmitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '15px',
    cursor: 'pointer',
  },
  lockedLink: {
    color: '#3b82f6',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default RequestDetail;